import { useModal } from "@newsela/angelou";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";

import { VIEW_PAGE_ASSIGN_GRAPHIC_ORGANIZERS_FEATURE } from "@/assignments/constants/experiments";
import useGetExportables from "@/assignments/useGetExportables";
import { GRAPHIC_ORGANIZER_PARAM } from "@/components/AIComponents/ExportablesModal";
import {
  TEMPLATE_REGISTRY,
  type TemplateDefinition,
} from "@/components/AIComponents/ExportablesModal/templates/registry";
import useHasFeature from "@/features/useHasFeature";
import { isSupportedEnglishContent } from "@/utils/generatedContent";
import { ExportablesContext } from "./useExportablesModalContext";

import type {
  ExportablesModalCommonProps,
  ExportablesModalContextValue,
} from "./ExportablesModalContext.types";

interface ExportablesModalContextProps extends ExportablesModalCommonProps {
  children: React.ReactNode;
  isStudent: boolean;
}

const ExportablesModalContext = ({
  children,
  content,
  contentMetadata = [],
  currentLevel,
  isStudent,
  levelId,
}: ExportablesModalContextProps) => {
  const contentId = content.id;
  const contentType = content.content_type;

  const router = useRouter();
  const { pathname, query } = router;

  const [modalProps, modalSetActive, modalTriggerRef] = useModal(false);
  const [formData, setFormData] = useState<any>(null);
  const [selectedExportable, setSelectedExportable] = useState(
    TEMPLATE_REGISTRY.AnticipationGuide,
  );

  // TODO: filter list of templates based on student assignments [LESS-?]
  const templateList: TemplateDefinition<any>[] = isStudent
    ? [
        TEMPLATE_REGISTRY.AnticipationGuide,
        TEMPLATE_REGISTRY.CompareAndContrast,
      ]
    : Object.values(TEMPLATE_REGISTRY);

  const isContentSupported = isSupportedEnglishContent({
    contentType,
    currentLevel,
    contentMetadata,
  });

  const [isInAssignGraphicOrganizersExperiment] = useHasFeature(
    VIEW_PAGE_ASSIGN_GRAPHIC_ORGANIZERS_FEATURE,
  );

  const { data: exportablesMetadata, loading: exportablesLoading } =
    useGetExportables({
      articleId: contentId,
      levelId,
      skip: !isContentSupported,
    }) || {};

  const isLoading = exportablesLoading || exportablesMetadata === null;

  const handleModalClose = () => {
    modalSetActive(false);

    const newQuery = { ...query };
    delete newQuery[GRAPHIC_ORGANIZER_PARAM];

    router.replace({ pathname, query: newQuery }, undefined, { shallow: true });
  };

  const handleTemplateSelection = (template?: TemplateDefinition<any>) => {
    const templateToSelect = template || templateList[0];

    if (!templateToSelect) return;

    setSelectedExportable(templateToSelect);

    const newQuery = {
      ...query,
      [GRAPHIC_ORGANIZER_PARAM]: templateToSelect.seti_identifier,
    };

    router.replace({ pathname, query: newQuery }, undefined, { shallow: true });
  };

  useEffect(() => {
    setFormData({
      ...exportablesMetadata?.[selectedExportable.seti_identifier],
      articleShortTitle: currentLevel?.short_title,
    });
  }, [
    currentLevel?.short_title,
    selectedExportable?.seti_identifier,
    JSON.stringify(exportablesMetadata),
  ]);

  useEffect(() => {
    const graphicOrganizerParam = query[GRAPHIC_ORGANIZER_PARAM];

    if (graphicOrganizerParam && typeof graphicOrganizerParam === "string") {
      const template = templateList.find(
        (t) => t.seti_identifier === graphicOrganizerParam,
      );

      if (template) {
        setSelectedExportable(template);
        modalSetActive(true);
      }
    }
  }, [query, modalSetActive]);

  const contextValue: ExportablesModalContextValue = {
    content,
    contentMetadata,
    currentLevel,
    formData,
    handleModalClose,
    handleTemplateSelection,
    isContentSupported,
    isInAssignGraphicOrganizersExperiment,
    isLoading,
    levelId,
    modalProps,
    modalSetActive,
    modalTriggerRef,
    selectedExportable,
    setFormData,
    setSelectedExportable,
    templateList,
  };

  return (
    <ExportablesContext.Provider value={contextValue}>
      {children}
    </ExportablesContext.Provider>
  );
};

export default ExportablesModalContext;
